// Modal admin: calibração OMR com PDF modelo local (antes do lote).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { styles } from '../../styles/globalStyles';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';
import { ExamObjectiveOverlayPanel } from './ExamObjectiveOverlayPanel';
import { getButtonStyle } from '../../constants/statusTranslations';
import { OVERLAY_DEFAULTS } from '../../utils/examObjectiveOverlayPrefs';

export const ExamOmrCalibrationModal = React.memo(
  ({
    isOpen,
    onClose,
    processId,
    definitiveAnswerKeyReady,
    onCalibrationUpdated,
    initialReferenceFilename = null,
    initialReview = null,
    previewVersion = 0,
    previewStaging = false,
    onRequestReplaceFile,
  }) => {
    const { addNotification } = useNotifications();
    const fileInputRef = useRef(null);
    const [calibration, setCalibration] = useState(null);
    const [loadingCal, setLoadingCal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [referenceFilename, setReferenceFilename] = useState(initialReferenceFilename || '');
    const [review, setReview] = useState(initialReview);
    const [prefs, setPrefs] = useState({ ...OVERLAY_DEFAULTS, scaleX: 100 });
    const [hasStaging, setHasStaging] = useState(Boolean(previewStaging));

    const loadCalibration = useCallback(async () => {
      if (!processId || !isOpen) return;
      setLoadingCal(true);
      const { data, error } = await apiClient.getProcessExamOmrCalibration(processId);
      setLoadingCal(false);
      if (error) {
        addNotification('Calibração OMR', error, 'warning');
        return;
      }
      setCalibration(data);
      onCalibrationUpdated?.(data);
      if (data?.active || data?.reference_filename) {
        if (!previewStaging && !initialReview?.staging) {
          setPrefs({
            opacity: OVERLAY_DEFAULTS.opacity,
            offsetX: data.offsetX ?? 0,
            offsetY: data.offsetY ?? 0,
            scale: data.scale ?? 100,
            scaleX: data.scaleX ?? 100,
          });
        }
        if (data.reference_filename && !referenceFilename) {
          setReferenceFilename(data.reference_filename);
        }
      }
    }, [processId, isOpen, addNotification, onCalibrationUpdated, referenceFilename, previewStaging, initialReview]);

    useEffect(() => {
      if (isOpen) loadCalibration();
    }, [isOpen, loadCalibration]);

    useEffect(() => {
      if (initialReferenceFilename) setReferenceFilename(initialReferenceFilename);
    }, [initialReferenceFilename]);

    useEffect(() => {
      if (initialReview) {
        setReview(initialReview);
        setHasStaging(Boolean(initialReview?.staging || previewStaging));
        const sug = initialReview?.suggested_calibration;
        if (sug) {
          setPrefs({
            opacity: OVERLAY_DEFAULTS.opacity,
            offsetX: sug.offsetX ?? 0,
            offsetY: sug.offsetY ?? 0,
            scale: sug.scale ?? 100,
            scaleX: sug.scaleX ?? 100,
          });
        }
      }
    }, [initialReview, previewStaging]);

    const handleClose = useCallback(async () => {
      if (hasStaging && processId) {
        await apiClient.discardProcessExamOmrCalibrationStaging(processId);
      }
      onClose();
    }, [hasStaging, processId, onClose]);

    const handleSave = useCallback(async () => {
      if (!processId || !referenceFilename) {
        addNotification('Calibração', 'Envie uma folha modelo (PDF) antes de salvar.', 'warning');
        return;
      }
      setSaving(true);
      const { data, error } = await apiClient.putProcessExamOmrCalibration(processId, {
        active: true,
        reference_filename: referenceFilename,
        offsetX: prefs.offsetX,
        offsetY: prefs.offsetY,
        scale: prefs.scale,
        scaleX: prefs.scaleX,
      });
      setSaving(false);
      if (error) {
        addNotification('Erro', error, 'error');
        return;
      }
      setCalibration(data);
      onCalibrationUpdated?.(data);
      setHasStaging(false);
      addNotification(
        'Calibração OMR salva',
        'Parâmetros gravados. Agora envie o lote de folhas e use "Reprocessar notas automáticas".',
        'success'
      );
    }, [processId, referenceFilename, prefs, addNotification, onCalibrationUpdated]);

    const handleDisable = useCallback(async () => {
      if (!processId) return;
      setSaving(true);
      const { data, error } = await apiClient.putProcessExamOmrCalibration(processId, {
        active: false,
        reference_filename: referenceFilename || calibration?.reference_filename || null,
        offsetX: prefs.offsetX,
        offsetY: prefs.offsetY,
        scale: prefs.scale,
        scaleX: prefs.scaleX,
      });
      setSaving(false);
      if (error) {
        addNotification('Erro', error, 'error');
        return;
      }
      setCalibration(data);
      onCalibrationUpdated?.(data);
      addNotification('Calibração OMR', 'Desativada — leitura usa só marcas/heurística.', 'info');
    }, [processId, calibration, referenceFilename, prefs, addNotification, onCalibrationUpdated]);

    const handleReplaceFile = useCallback(() => {
      if (onRequestReplaceFile) {
        onRequestReplaceFile();
        return;
      }
      fileInputRef.current?.click();
    }, [onRequestReplaceFile]);

    if (!processId) return null;

    const hasModel = Boolean(referenceFilename && review);

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Calibrar OMR — folha modelo"
        large="conference"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f && onRequestReplaceFile) onRequestReplaceFile(f);
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            maxHeight: 'calc(94vh - 100px)',
            overflow: 'hidden',
          }}
        >
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 8 }}>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
              <strong>1.</strong> Escolha um scan de referência (mesmo equipamento do lote).{' '}
              <strong>2.</strong> Confira o alinhamento do gabarito vermelho.{' '}
              <strong>3.</strong> Clique <strong>Salvar calibração</strong> — só então o selo verde
              e a leitura do lote passam a usar esta folha.{' '}
              <strong>4.</strong> Envie o lote e reprocesse o OMR.
            </p>

            {hasStaging && (
              <p
                style={{
                  fontSize: 12,
                  color: '#B45309',
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: '#FFFBEB',
                  borderRadius: 8,
                  border: '1px solid #FDE68A',
                }}
              >
                Pré-visualização — ainda <strong>não gravada</strong>. Feche sem salvar e o selo
                verde mantém a calibração anterior.
              </p>
            )}

            {review?.suggested_calibration && (
              <p
                style={{
                  fontSize: 12,
                  color: '#1D4ED8',
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: '#EFF6FF',
                  borderRadius: 8,
                  border: '1px solid #BFDBFE',
                }}
              >
                Ajuste automático calculado a partir das bolhas desta folha (
                {review.suggested_calibration.sample_count ?? '?'} amostras). Revise e salve.
              </p>
            )}

            {calibration?.active && (
              <p
                style={{
                  fontSize: 12,
                  color: '#166534',
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: '#ECFDF5',
                  borderRadius: 8,
                  border: '1px solid #A7F3D0',
                }}
              >
                Calibração ativa
                {calibration.reference_filename
                  ? ` · folha modelo: ${calibration.reference_filename}`
                  : ''}
                {calibration.updated_at
                  ? ` · ${new Date(calibration.updated_at).toLocaleString('pt-BR')}`
                  : ''}
              </p>
            )}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 12, color: '#374151' }}>
                Folha modelo:{' '}
                <strong>{referenceFilename || '— nenhum PDF carregado —'}</strong>
              </span>
              <button
                type="button"
                disabled={!definitiveAnswerKeyReady || saving}
                onClick={handleReplaceFile}
                style={getButtonStyle('secondary', 'small')}
              >
                Escolher PDF…
              </button>
            </div>

            {loadingCal ? (
              <p style={{ fontSize: 13, color: '#64748B' }}>Carregando calibração…</p>
            ) : hasModel && definitiveAnswerKeyReady ? (
              <ExamObjectiveOverlayPanel
                calibrationProcessId={processId}
                processId={processId}
                variant="large"
                showScaleX
                persistLocalPrefs={false}
                controlledPrefs={prefs}
                onControlledPrefsChange={setPrefs}
                externalReview={review}
                previewVersion={previewVersion}
                previewStaging={hasStaging}
              />
            ) : (
              <p style={{ fontSize: 12, color: '#B45309' }}>
                {!definitiveAnswerKeyReady
                  ? 'Cadastre o gabarito definitivo antes de calibrar.'
                  : 'Escolha um PDF de folha escaneada para iniciar a calibração.'}
              </p>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              flexShrink: 0,
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid #E5E7EB',
              justifyContent: 'flex-end',
              background: '#fff',
            }}
          >
            {calibration?.active && (
              <button
                type="button"
                disabled={saving}
                onClick={handleDisable}
                style={{ ...getButtonStyle('secondary', 'small'), marginRight: 'auto' }}
              >
                Desativar calibração
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              style={{ ...styles.button, ...styles.buttonSecondary }}
              disabled={saving}
            >
              Fechar
            </button>
            <button
              type="button"
              disabled={saving || loadingCal || !hasModel || !definitiveAnswerKeyReady}
              onClick={handleSave}
              style={{ ...styles.button, ...styles.buttonPrimary, opacity: saving ? 0.7 : 1 }}
            >
              {saving ? 'Salvando…' : 'Salvar calibração'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }
);

ExamOmrCalibrationModal.displayName = 'ExamOmrCalibrationModal';
